import { BrowserStorage } from '@app/utils/BrowserStorage';
import { CUSTOM_EVENTS } from '@mgCommon/constants/constants.js';
import { Popover } from 'bootstrap';
import insertTextAtCursor from 'insert-text-at-cursor';

const { tinymce } = window;

const sessionStorage = new BrowserStorage('session');

export function isArrayLike(item) {
  return (
    Array.isArray(item) ||
    (!!item &&
      typeof item === 'object' &&
      // eslint-disable-next-line no-restricted-globals
      Object.prototype.hasOwnProperty.call(item, length) &&
      typeof item.length === 'number' &&
      item.length > 0 &&
      item.length - 1 in item)
  );
}

export function isObject(item) {
  return typeof item === 'object' && item !== null && !Array.isArray(item);
}

export function getCbBaseURL() {
  return window.MESSAGE_GATEWAY_CONFIG?.hosts?.cbBaseUrl || '';
}

export function getCbVarible() {
  return window.MESSAGE_GATEWAY_CONFIG?.cbVarible || '';
}

export function prepareUrl(url, ...params) {
  let newUrl = url;
  params.forEach(item => {
    newUrl = newUrl.replace(item.variable, item.value);
  });
  return newUrl;
}

export function stringToHTML({ str, classes }) {
  const elem = document.createElement('div');
  elem.innerHTML = str;
  elem.classList.add(...classes);
  return elem;
}

export function setUpEmojiPickerPopovers({ cxt, opts = {} }) {
  const emojiPopoverList = Array.from(cxt.renderRoot.querySelectorAll('[data-bs-toggle="emoji-popover"]'));
  const initPopover = (element, options) => new Popover(element, options);
  emojiPopoverList.forEach(popoverTriggerEl => {
    const tmpOpts = { ...opts };
    if (popoverTriggerEl.hasAttribute('data-bs-content-id')) {
      tmpOpts.content = cxt.renderRoot.querySelector(
        `.${popoverTriggerEl.getAttribute('data-bs-content-id')} emoji-picker`
      );
      tmpOpts.container = opts.container ? popoverTriggerEl : 'body';
      tmpOpts.placement = opts.placement || 'auto';
    }
    initPopover(popoverTriggerEl, tmpOpts);
    popoverTriggerEl.addEventListener('emoji-click', evt => {
      const elInput = cxt.renderRoot.querySelector(`#${popoverTriggerEl.getAttribute('data-bs-input-id')}`);
      insertTextAtCursor(elInput, evt.detail.unicode);
    });
  });
}

export function isValidHttpUrl(string) {
  try {
    const newUrl = new URL(string);
    return newUrl.protocol === 'http:' || newUrl.protocol === 'https:';
  } catch (err) {
    return false;
  }
}

export function deepEqual(object1, object2) {
  const keys1 = Object.keys(object1);
  const keys2 = Object.keys(object2);

  if (keys1.length !== keys2.length) {
    return false;
  }

  for (const key of keys1) {
    const val1 = object1[key];
    const val2 = object2[key];
    const areObjects = isObject(val1) && isObject(val2);
    if ((areObjects && !deepEqual(val1, val2)) || (!areObjects && val1 !== val2)) {
      return false;
    }
  }

  return true;
}

export function generateUUID() {
  return crypto.randomUUID();
}

export function insertContentToWysiwyg({ source = '', value = '' }) {
  const editorId = sessionStorage.get(source, '');
  const editor = tinymce?.get(editorId);
  editor?.execCommand('mceInsertContent', false, value);
}

export function setContentToWysiwyg({ source = '', value = '' }) {
  const editorId = sessionStorage.get(source, '');
  const editor = tinymce?.get(editorId);
  editor?.setContent(value);
}

export function isValidDate({ date = null }) {
  const d = new Date(date);
  return !Number.isNaN(d) && !Number.isNaN(d.getTime());
}

export function convertEpochToDate({ epoch = null }) {
  return typeof epoch === 'number' ? new Date(epoch * 1000) : null;
}

export function convertDateToEpoch({ date = null }) {
  return Math.floor(date.getTime() / 1000);
}

export function extractDateParts({ date = null }) {
  const year = String(date.getFullYear());
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');

  return { year, month, day, hours, minutes, seconds };
}

export function formatDateToYmdHms({ date = null }) {
  if (!isValidDate(date)) {
    return null;
  }
  const { year, month, day, hours, minutes, seconds } = extractDateParts({ date }) ?? {};

  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
}

export function convertEpochToReadableDate({ epoch = null }) {
  const date = convertEpochToDate({ epoch });
  if (!date) {
    return null;
  }

  return formatDateToYmdHms({ date });
}

export function convertReadableDateToEpoch({ dateString = '' }) {
  if (!dateString || typeof dateString !== 'string' || !isValidDate({ date: dateString })) {
    return null;
  }
  const date = new Date(dateString);
  const epoch = convertDateToEpoch({ date });

  return epoch;
}

export function preventNonNumericInput(event) {
  const { key } = event;

  // Allow navigation and input control keys
  if (
    key === 'Backspace' ||
    key === 'Tab' ||
    key === 'Enter' ||
    key === 'Escape' ||
    key === 'ArrowLeft' ||
    key === 'ArrowRight' ||
    (key >= '0' && key <= '9') ||
    (key >= 'NumPad0' && key <= 'NumPad9')
  ) {
    return;
  }

  // Prevent non-numeric input
  event.preventDefault();
}

export function dispatchShowCaToastEvent({ cxt = null, toastType = '', message = '', timeout = 0 }) {
  const options = {
    detail: {
      toastType,
      message,
      timeout,
    },
  };
  const event = new CustomEvent(CUSTOM_EVENTS.SHOW_CA_TOAST, {
    ...(cxt ? { bubbles: true, composed: true } : {}),
    ...options,
  });
  cxt ? cxt.dispatchEvent(event) : window.dispatchEvent(event);
}

export function setUpDynamicParametersPopovers({ cxt, opts = {} }) {
  const dynamicParameterPopoverList = Array.from(
    cxt.renderRoot.querySelectorAll('[data-bs-toggle="dynamic-parameters-popover"]')
  );
  const initPopover = (element, options) => new Popover(element, options);
  dynamicParameterPopoverList.forEach(popoverTriggerEl => {
    const tmpOpts = { ...opts };
    if (popoverTriggerEl.hasAttribute('data-bs-content-id')) {
      tmpOpts.content = cxt.renderRoot.querySelector(
        `.${popoverTriggerEl.getAttribute('data-bs-content-id')} .app-message-template-parameter-list`
      );
      tmpOpts.container = opts.container ? popoverTriggerEl : 'body';
      tmpOpts.placement = opts.placement || 'auto';
    }
    initPopover(popoverTriggerEl, tmpOpts);
    popoverTriggerEl.addEventListener(CUSTOM_EVENTS.MESSAGE_TEMPLATE_PARAMETER_CLICK, evt => {
      const elInput = cxt.renderRoot.querySelector(`#${popoverTriggerEl.getAttribute('data-bs-input-id')}`);
      const placeholder = `[[${evt.detail?.paramName}]]`;
      insertTextAtCursor(elInput, placeholder);
    });
  });
}

export function encodeUrl(url) {
  return window.btoa(window.encodeURI(url));
}

export function decodeUrl(url) {
  return window.decodeURI(window.atob(url));
}
